import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  buyerSubmissionsApi,
  type BuyerRecordVersionEntryDto,
} from "@/api/buyers";
import {
  Button,
  Input,
  Textarea,
  Timestamp,
  useToast,
} from "@/design-system/components/ui";
import { buyerSubmissionKeys } from "@/hooks/buyers/buyerKeys";
import { calendarDateInputToIso, isoDateInTz, useUserTimeZone } from "@/utils/datetime";

/**
 * The append-only note thread on one version of a paperwork record.
 *
 * ============================================================================
 * 🔴 WHY THIS IS A LIST AND NOT A TEXTAREA
 * ============================================================================
 *
 * <p>The NDA authorisation note used to be one nullable column, rendered as one
 * field, edited by overwriting it. <b>NDA authorisation is what gates presenting
 * a buyer to a listing broker</b> — so a field whose history can be silently
 * rewritten is the wrong primitive for it. "Who said we were authorised, and
 * when did they say it" is precisely the question that gets asked afterwards,
 * and a single column cannot answer it: every save destroyed the previous
 * answer, with nothing recording that it had.</p>
 *
 * <p>So a correction EDITS the latest entry and a new event ADDS one, and the
 * two are different acts rather than the same overwrite. Each entry is dated by
 * when the thing HAPPENED (back-datable — a call on Tuesday logged on Thursday
 * reads Tuesday) and attributed to whoever wrote it.</p>
 *
 * <p>🔴 <b>`canEdit` comes from the server and is never recomputed here.</b> The
 * rule — your own most recent entry on this version, and only if you may write
 * the record — is decided by `CreatedAt` rather than the back-datable
 * `OccurredAtUtc`, so an author cannot reach an older entry by post-dating a
 * newer one. Deriving that locally would be a second statement of the rule, and
 * the first time the two disagreed the surface would offer an action the service
 * refuses.</p>
 *
 * <p><b>Newest first, in the order the server sent.</b> It orders by
 * `OccurredAtUtc` descending with the id as a tiebreak; re-sorting here would be
 * a second definition of the thread's order.</p>
 */
export function RecordEntryThread({
  versionId,
  entries,
  canWrite,
  compose = true,
  "data-testid": testId,
}: {
  versionId: string;
  entries: BuyerRecordVersionEntryDto[];
  canWrite: boolean;
  /**
   * Whether to offer the composer. Superseded versions render their thread
   * read-only-ish: their history stays visible and an author may still correct
   * their own last word, but a NEW note belongs on the NDA that is actually in
   * force.
   */
  compose?: boolean;
  "data-testid"?: string;
}) {
  return (
    <div className="grid gap-space-3" data-testid={testId}>
      {canWrite && compose && <EntryComposer versionId={versionId} />}

      {entries.length === 0 ? (
        <p className="text-caption text-gray-mid m-0" data-testid="record-entries-empty">
          No notes on this NDA yet.
        </p>
      ) : (
        <ol className="grid gap-space-2 list-none p-0 m-0">
          {entries.map((e) => (
            <EntryRow key={e.id} entry={e} />
          ))}
        </ol>
      )}
    </div>
  );
}

function EntryComposer({ versionId }: { versionId: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const tz = useUserTimeZone();
  const [body, setBody] = useState("");
  const [occurred, setOccurred] = useState(() => today(tz));

  const add = useMutation({
    mutationFn: () =>
      buyerSubmissionsApi.addRecordEntry(versionId, {
        body: body.trim(),
        occurredAtUtc: asInstant(occurred),
      }),
    onSuccess: () => {
      // The whole submission prefix: the record read holds the thread, and the
      // detail read's mirror of the authorisation note is served from the same
      // write.
      void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
      setBody("");
      setOccurred(today(tz));
      toast.success("Note added.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const future = occurred > today(tz);

  return (
    <div className="grid gap-space-2" data-testid="record-entry-composer">
      <label htmlFor={`entry-body-${versionId}`} className="block text-caption text-gray-mid">
        Add a note
      </label>
      <Textarea
        id={`entry-body-${versionId}`}
        data-testid="record-entry-body"
        rows={2}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="e.g. sent to the broker Tuesday, countersigned copy still outstanding"
      />
      <div className="flex flex-wrap items-end gap-space-2">
        <div className="grid gap-space-1">
          <label
            htmlFor={`entry-date-${versionId}`}
            className="block text-caption text-gray-mid"
          >
            When it happened
          </label>
          <Input
            id={`entry-date-${versionId}`}
            data-testid="record-entry-date"
            type="date"
            className="w-auto"
            value={occurred}
            max={today(tz)}
            onChange={(e) => setOccurred(e.target.value)}
          />
        </div>
        <Button
          variant="secondary"
          size="sm"
          data-testid="record-entry-submit"
          disabled={!body.trim() || future || add.isPending}
          onClick={() => add.mutate()}
        >
          {add.isPending ? "Adding…" : "Add note"}
        </Button>
      </div>
      {future && (
        <p className="text-caption text-status-warning-ink m-0">
          A note records something that has already happened.
        </p>
      )}
    </div>
  );
}

function EntryRow({ entry }: { entry: BuyerRecordVersionEntryDto }) {
  const qc = useQueryClient();
  const toast = useToast();
  const tz = useUserTimeZone();
  const [editing, setEditing] = useState(false);
  const [confirmingRetract, setConfirmingRetract] = useState(false);
  const [body, setBody] = useState(entry.body);
  const [occurred, setOccurred] = useState(entry.occurredAtUtc.slice(0, 10));

  const update = useMutation({
    mutationFn: () =>
      buyerSubmissionsApi.updateRecordEntry(entry.id, {
        body: body.trim(),
        occurredAtUtc: asInstant(occurred),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
      setEditing(false);
      toast.success("Note corrected.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const retract = useMutation({
    mutationFn: () => buyerSubmissionsApi.deleteRecordEntry(entry.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
      setConfirmingRetract(false);
      toast.success("Note retracted.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <li
      className="rounded-md border border-border-light bg-white-warm p-space-3"
      data-testid="record-entry"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-space-2">
        <span className="text-caption text-gray-mid">
          <Timestamp value={entry.occurredAtUtc} />
          {" · "}
          {entry.authorName ?? "Unknown author"}
          {wasEdited(entry) && (
            <span className="italic" data-testid="record-entry-edited">
              {" · edited"}
            </span>
          )}
        </span>

        {/* 🔴 Offered ONLY where the server said so. Nothing here re-derives the
            rule; `canEdit` is the same statement the write path enforces. */}
        {entry.canEdit && !editing && !confirmingRetract && (
          <span className="flex items-center gap-space-2">
            <Button
              variant="text"
              size="sm"
              className="px-0"
              data-testid="record-entry-edit"
              onClick={() => setEditing(true)}
            >
              Edit
            </Button>
            <Button
              variant="text"
              size="sm"
              className="px-0"
              data-testid="record-entry-retract"
              onClick={() => setConfirmingRetract(true)}
            >
              Retract
            </Button>
          </span>
        )}
      </div>

      {editing ? (
        <div className="grid gap-space-2 mt-space-2">
          <Textarea
            rows={2}
            aria-label="Correct this note"
            data-testid="record-entry-edit-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <div className="flex flex-wrap items-center gap-space-2">
            <Input
              type="date"
              aria-label="Correct when it happened"
              data-testid="record-entry-edit-date"
              className="w-auto"
              value={occurred}
              max={today(tz)}
              onChange={(e) => setOccurred(e.target.value)}
            />
            <Button
              variant="secondary"
              size="sm"
              data-testid="record-entry-edit-save"
              disabled={!body.trim() || occurred > today(tz) || update.isPending}
              onClick={() => update.mutate()}
            >
              {update.isPending ? "Saving…" : "Save"}
            </Button>
            <Button
              variant="text"
              size="sm"
              onClick={() => {
                setBody(entry.body);
                setOccurred(entry.occurredAtUtc.slice(0, 10));
                setEditing(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-small text-onyx m-0 mt-space-1 whitespace-pre-wrap">
          {entry.body}
        </p>
      )}

      {/* Inline rather than a dialog, deliberately: this row can render inside a
          collapsed <details> of superseded versions, and confirmation that lives
          in the row cannot be separated from the thing it is confirming. */}
      {confirmingRetract && (
        <div className="flex flex-wrap items-center gap-space-2 mt-space-2">
          <span className="text-caption text-gray-mid">Retract this note?</span>
          <Button
            variant="secondary"
            size="sm"
            data-testid="record-entry-retract-confirm"
            disabled={retract.isPending}
            onClick={() => retract.mutate()}
          >
            {retract.isPending ? "Retracting…" : "Yes, retract"}
          </Button>
          <Button variant="text" size="sm" onClick={() => setConfirmingRetract(false)}>
            Cancel
          </Button>
        </div>
      )}
    </li>
  );
}

/**
 * Has this note been corrected since it was written?
 *
 * <p>🔴 <b>NOT `modifiedAt != null`.</b> The service stamps `ModifiedAt = now`
 * alongside `CreatedAt` when the entry is CREATED — both from the same variable
 * — so a nullity test marks every note ever written as edited. The two are
 * byte-identical strings on the wire until an edit moves one of them, which is
 * what makes the inequality the honest test.</p>
 */
export function wasEdited(entry: BuyerRecordVersionEntryDto): boolean {
  return !!entry.modifiedAt && entry.modifiedAt !== entry.createdAt;
}

/**
 * Today as `YYYY-MM-DD` in the VIEWER'S zone, which is what a `type="date"`
 * input wants.
 *
 * <p>Read through `isoDateInTz` rather than shifting an instant by the
 * BROWSER's offset: that spelling produces a third "today" agreeing with
 * neither UTC nor the app's zone, and it disagrees with every other date this
 * product renders. The seed, the `max=` bound and the submit gate all call
 * this one function, so they cannot drift apart.</p>
 */
function today(tz: string): string {
  return isoDateInTz(new Date(), tz);
}

/**
 * A picked calendar day as the instant the wire expects.
 *
 * Noon UTC, not midnight: a wall-calendar day stored at `T00:00:00Z` renders as
 * the PREVIOUS day through any tz-aware formatter west of Greenwich. Noon
 * survives being re-rendered in every zone from UTC-11 to UTC+11 as the day
 * that was actually typed.
 */
function asInstant(day: string): string {
  return calendarDateInputToIso(day)!;
}
